// MOCK: whole component. Swap for Google Identity Services; keep the props contract.
/**
 * Simulated Google OAuth consent flow.
 *
 * Renders a faithful *look* of the Google sign-in popup so prospects recognise
 * the shape of the integration, while never contacting Google:
 *   - no real Google branding assets; the mark below is drawn here
 *   - the mock URL bar shows accounts.google.example, a reserved test TLD
 *   - there is no password field anywhere, by construction
 *   - a persistent, non-dismissible DEMO pill sits in the window chrome
 *
 * The caller only sees `onSuccess(account, property)` / `onError(code)`, so the
 * real implementation drops in without the wizard changing.
 */
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Check, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Copy, Loader2, Lock, Plus, Search } from "lucide-react";

import { COPY } from "./copy";
import { ProviderMark } from "./ProviderMark";
import {
    ANALYTICS_READONLY_SCOPE,
    MOCK_ACCOUNTS,
    MOCK_DELAYS,
    MOCK_OAUTH_URL,
    MOCK_PROPERTY_ACCOUNTS,
    MockGa4Property,
    MockGoogleAccount,
    MockPropertyAccount,
    OAuthErrorCode,
    PROPERTY_SEARCH_THRESHOLD,
    countProperties
} from "./fixtures/oauth-demo";

type Stage = "IDLE" | "LAUNCHING" | "ACCOUNT_CHOOSER" | "CONSENT" | "PROCESSING" | "SUCCESS" | "ERROR";

interface State {
    stage: Stage;
    account: MockGoogleAccount | null;
    error: OAuthErrorCode | null;
    /** True while the wizard-side "Connecting…" line shows, after the popup exits. */
    finalising: boolean;
}

type Action =
    | { type: "LAUNCH" }
    | { type: "POPUP_READY" }
    | { type: "PICK_ACCOUNT"; account: MockGoogleAccount }
    | { type: "CONSENT_GRANTED" }
    | { type: "FINALISE" }
    | { type: "SUCCEED" }
    | { type: "FAIL"; error: OAuthErrorCode }
    | { type: "RESET" }
    | { type: "SWITCH_ACCOUNT" };

const INITIAL: State = { stage: "IDLE", account: null, error: null, finalising: false };

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "LAUNCH":
            return { ...INITIAL, stage: "LAUNCHING" };
        case "POPUP_READY":
            return { ...state, stage: "ACCOUNT_CHOOSER" };
        case "PICK_ACCOUNT":
            return { ...state, stage: "CONSENT", account: action.account };
        case "CONSENT_GRANTED":
            return { ...state, stage: "PROCESSING" };
        case "FINALISE":
            return { ...state, finalising: true };
        case "SUCCEED":
            return { ...state, stage: "SUCCESS", finalising: false, error: null };
        case "FAIL":
            return { ...state, stage: "ERROR", finalising: false, error: action.error };
        case "SWITCH_ACCOUNT":
            return { ...INITIAL, stage: "LAUNCHING" };
        case "RESET":
            return INITIAL;
        default:
            return state;
    }
}

const popupIsOpen = (stage: Stage) =>
    stage === "LAUNCHING" || stage === "ACCOUNT_CHOOSER" || stage === "CONSENT" || stage === "PROCESSING";

// ── Simulated popup ──────────────────────────────────────────────────────────

function useReducedMotion(): boolean {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const apply = () => setReduced(mq.matches);
        apply();
        mq.addEventListener("change", apply);
        return () => mq.removeEventListener("change", apply);
    }, []);
    return reduced;
}

function MockPopup({
    onScrimClose,
    children
}: {
    onScrimClose: () => void;
    children: React.ReactNode;
}) {
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const reducedMotion = useReducedMotion();

    // Focus trap: hold focus inside the popup for as long as it's open.
    useEffect(() => {
        const node = dialogRef.current;
        if (!node) return;

        const focusables = () =>
            Array.from(
                node.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                )
            ).filter((el) => el.offsetParent !== null);

        focusables()[0]?.focus();

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                onScrimClose();
                return;
            }
            if (e.key !== "Tab") return;
            const list = focusables();
            if (list.length === 0) return;
            const first = list[0];
            const last = list[list.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", onKeyDown, true);
        return () => document.removeEventListener("keydown", onKeyDown, true);
    }, [onScrimClose]);

    if (typeof document === "undefined") return null;

    return createPortal(
        // z above the wizard modal (z-[1000]) — this layers on top, never navigates.
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/50" onClick={onScrimClose} aria-hidden />
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-label={COPY.wizard.oauth.windowLabel}
                style={{ width: 450, maxWidth: "100%", height: 600, maxHeight: "92vh" }}
                className={`relative flex flex-col overflow-hidden rounded-xl bg-white shadow-2xl ${
                    reducedMotion ? "" : "ra-popup-enter"
                }`}>
                {/* Fake browser chrome — inert by design. */}
                <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
                    <ChevronLeft className="h-3.5 w-3.5 text-gray-300" strokeWidth={2} aria-hidden />
                    <ChevronRight className="h-3.5 w-3.5 text-gray-300" strokeWidth={2} aria-hidden />
                    <div className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md bg-white px-2 py-1 ring-1 ring-gray-200">
                        <Lock className="h-3 w-3 flex-none text-gray-400" strokeWidth={2} aria-hidden />
                        <span className="truncate text-[11px] text-gray-500">{MOCK_OAUTH_URL}</span>
                    </div>
                    <span className="flex-none rounded-full bg-gray-200 px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-gray-600">
                        {COPY.wizard.oauth.demoPill}
                    </span>
                </div>
                <div className="flex-1 overflow-y-auto px-7 py-7">{children}</div>
            </div>
        </div>,
        document.body
    );
}

// ── Popup screens ────────────────────────────────────────────────────────────

function AccountChooser({
    onPick,
    onUseAnother
}: {
    onPick: (a: MockGoogleAccount) => void;
    onUseAnother: () => void;
}) {
    const c = COPY.wizard.oauth.chooser;
    return (
        <div>
            <ProviderMark className="h-6 w-6" />
            <h2 className="mt-5 text-[19px] font-normal text-gray-900">{c.title}</h2>
            <p className="mt-1 text-[13px] text-gray-600">{c.subtitle}</p>

            <ul className="mt-6 divide-y divide-gray-100 border-y border-gray-100">
                {MOCK_ACCOUNTS.map((a) => (
                    <li key={a.id}>
                        <button
                            type="button"
                            onClick={() => onPick(a)}
                            className="flex w-full items-center gap-3 py-3 text-left transition-colors hover:bg-gray-50">
                            <span
                                className="flex h-8 w-8 flex-none items-center justify-center rounded-full text-[11.5px] font-semibold text-white"
                                style={{ backgroundColor: a.color }}
                                aria-hidden>
                                {a.initials}
                            </span>
                            <span className="min-w-0">
                                <span className="block truncate text-[13.5px] text-gray-900">{a.name}</span>
                                <span className="block truncate text-[12px] text-gray-500">{a.email}</span>
                            </span>
                        </button>
                    </li>
                ))}
                <li>
                    <button
                        type="button"
                        onClick={onUseAnother}
                        className="flex w-full items-center gap-3 py-3 text-left transition-colors hover:bg-gray-50">
                        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full ring-1 ring-gray-200">
                            <Plus className="h-3.5 w-3.5 text-gray-500" strokeWidth={2} />
                        </span>
                        <span className="text-[13.5px] text-gray-900">{c.useAnother}</span>
                    </button>
                </li>
            </ul>
        </div>
    );
}

function ConsentScreen({
    account,
    scopes,
    onCancel,
    onConfirm
}: {
    account: MockGoogleAccount;
    scopes: string[];
    onCancel: () => void;
    onConfirm: () => void;
}) {
    const c = COPY.wizard.oauth.consent;
    const [scopeOpen, setScopeOpen] = useState(false);

    return (
        <div className="flex min-h-full flex-col">
            <ProviderMark className="h-6 w-6" />
            <h2 className="mt-5 text-[19px] font-normal leading-snug text-gray-900">{c.title}</h2>

            <div className="mt-3 inline-flex w-fit items-center gap-2 rounded-full border border-gray-200 px-2.5 py-1">
                <span
                    className="flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold text-white"
                    style={{ backgroundColor: account.color }}
                    aria-hidden>
                    {account.initials}
                </span>
                <span className="text-[12px] text-gray-700">{account.email}</span>
            </div>

            <p className="mt-6 text-[13px] text-gray-700">{c.allowIntro}</p>

            <div className="mt-3 rounded-lg border border-gray-200 p-3">
                <div className="flex gap-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-md bg-gray-100 text-[12px]">
                        📊
                    </span>
                    <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-medium text-gray-900">{c.scopeTitle}</p>
                        <p className="mt-0.5 text-[12px] leading-relaxed text-gray-600">{c.scopeBody}</p>
                        <button
                            type="button"
                            onClick={() => setScopeOpen((v) => !v)}
                            aria-expanded={scopeOpen}
                            className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-medium text-geekblue-600 hover:text-geekblue-700">
                            {c.scopeToggle}
                            <ChevronDown
                                className={`h-3 w-3 transition-transform ${scopeOpen ? "rotate-180" : ""}`}
                                strokeWidth={2}
                            />
                        </button>
                        {scopeOpen && (
                            <ul className="mt-2 space-y-1">
                                {scopes.map((s) => (
                                    <li
                                        key={s}
                                        className="break-all rounded bg-gray-50 px-2 py-1 font-mono text-[10.5px] text-gray-700">
                                        {s}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            <p className="mt-6 text-[13px] font-medium text-gray-900">{c.trustTitle}</p>
            <p className="mt-1.5 text-[12px] leading-relaxed text-gray-600">{c.trustBody}</p>

            <div className="mt-auto flex justify-end gap-2 pt-7">
                <button
                    type="button"
                    onClick={onCancel}
                    className="rounded-md px-4 py-2 text-[13px] font-medium text-geekblue-600 transition-colors hover:bg-geekblue-100">
                    {c.cancel}
                </button>
                <button
                    type="button"
                    onClick={onConfirm}
                    className="rounded-md bg-geekblue-600 px-5 py-2 text-[13px] font-medium text-white transition-colors hover:bg-geekblue-700">
                    {c.confirm}
                </button>
            </div>
        </div>
    );
}

function PopupSpinner({ label }: { label: string }) {
    return (
        <div className="flex h-full flex-col items-center justify-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            <p className="text-[13px] text-gray-600">{label}</p>
        </div>
    );
}

// ── Property picker ──────────────────────────────────────────────────────────

function PropertyIdChip({ propertyId }: { propertyId: string }) {
    const [copied, setCopied] = useState(false);
    return (
        <button
            type="button"
            // Stop the click bubbling to the surrounding radio label.
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigator.clipboard
                    ?.writeText(propertyId)
                    .then(() => {
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1600);
                    })
                    .catch(() => {
                        /* clipboard blocked — the id is still selectable on screen */
                    });
            }}
            title={COPY.wizard.step1.copyPropertyId}
            aria-label={`${COPY.wizard.step1.copyPropertyId} ${propertyId}`}
            className="inline-flex items-center gap-1 rounded font-mono text-[11px] text-gray-600 transition-colors hover:text-geekblue-600">
            {COPY.wizard.step1.propertyLabel(propertyId)}
            {copied ? (
                <Check className="h-3 w-3 text-emerald-600" strokeWidth={2.5} />
            ) : (
                <Copy className="h-2.5 w-2.5 opacity-50" strokeWidth={2} />
            )}
        </button>
    );
}

function PropertyPicker({
    accounts,
    selectedIds,
    onToggle
}: {
    accounts: MockPropertyAccount[];
    selectedIds: string[];
    onToggle: (p: MockGa4Property) => void;
}) {
    const [query, setQuery] = useState("");
    const total = useMemo(() => countProperties(accounts), [accounts]);
    const showSearch = total > PROPERTY_SEARCH_THRESHOLD;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return accounts;
        return accounts
            .map((a) => ({
                ...a,
                properties: a.properties.filter(
                    (p) => p.name.toLowerCase().includes(q) || p.propertyId.toLowerCase().includes(q)
                )
            }))
            .filter((a) => a.properties.length > 0);
    }, [accounts, query]);

    return (
        <div className="mt-5">
            <p className="text-[13px] font-medium text-gray-900">{COPY.wizard.step1.pickerHeading}</p>
            <p className="mt-0.5 text-[12.5px] text-gray-500">{COPY.wizard.step1.pickerSubheading}</p>

            {showSearch && (
                <div className="mt-3 flex items-center gap-2 rounded-lg border border-gray-200 px-2.5 py-1.5">
                    <Search className="h-3.5 w-3.5 flex-none text-gray-400" strokeWidth={2} />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={COPY.wizard.step1.pickerSearchPlaceholder}
                        className="w-full text-[12.5px] text-gray-900 focus:outline-none"
                    />
                </div>
            )}

            <div className="mt-3 space-y-4">
                {filtered.map((group) => (
                    <div key={group.accountId}>
                        <div className="flex items-baseline justify-between gap-3">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                {group.accountName}
                            </p>
                            <p className="font-mono text-[10.5px] text-gray-400">
                                {COPY.wizard.step1.accountLabel(group.accountId)}
                            </p>
                        </div>
                        <div className="mt-1.5 space-y-2">
                            {group.properties.map((p) => {
                                const selected = selectedIds.includes(p.id);
                                return (
                                    <label
                                        key={p.id}
                                        className={`flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors ${
                                            !p.supported
                                                ? "cursor-not-allowed border-gray-200 bg-gray-50 opacity-70"
                                                : selected
                                                ? "border-geekblue-400 bg-geekblue-100"
                                                : "border-gray-200 bg-white hover:border-gray-300"
                                        }`}>
                                        <input
                                            type="checkbox"
                                            className="mt-1 h-3.5 w-3.5 rounded accent-geekblue-600"
                                            disabled={!p.supported}
                                            checked={selected}
                                            onChange={() => onToggle(p)}
                                        />
                                        <span className="min-w-0 flex-1">
                                            <span className="flex items-baseline justify-between gap-2">
                                                <span className="truncate text-[13px] font-medium text-gray-900">
                                                    {p.name}
                                                </span>
                                                <span className="flex-none text-[10.5px] font-semibold uppercase tracking-wide text-gray-400">
                                                    {p.kind}
                                                </span>
                                            </span>
                                            {p.supported ? (
                                                <>
                                                    {/* Property ID is always visible — support asks for it by number. */}
                                                    <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-gray-500">
                                                        <PropertyIdChip propertyId={p.propertyId} />
                                                        <span aria-hidden>·</span>
                                                        <span>{p.timeZone}</span>
                                                        <span aria-hidden>·</span>
                                                        <span>{p.currencyCode}</span>
                                                    </span>
                                                    <span className="mt-0.5 block text-[11.5px] text-gray-500">
                                                        {COPY.wizard.step1.sessionsLast30d(p.sessionsLast30d ?? 0)}
                                                    </span>
                                                </>
                                            ) : (
                                                // Shown, not hidden: customers look for these and get
                                                // confused when they're missing entirely.
                                                <span className="mt-0.5 block text-[11.5px] text-gray-500">
                                                    {p.unsupportedReason ?? COPY.wizard.step1.uaTooltip}
                                                </span>
                                            )}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11.5px] text-gray-500">{COPY.wizard.step1.multiProperty}</p>
                <p className="text-[11.5px] font-medium text-gray-600">
                    {COPY.wizard.step1.selectedCount(selectedIds.length)}
                </p>
            </div>
            {selectedIds.length === 0 && (
                <p className="mt-1.5 text-[11.5px] text-amber-700">{COPY.wizard.step1.selectAtLeastOne}</p>
            )}
        </div>
    );
}

// ── Component ────────────────────────────────────────────────────────────────

export interface MockGoogleOAuthProps {
    scopes: string[];
    /** Fires on connect and on every property toggle — the wizard's only dependency. */
    onSuccess: (account: MockGoogleAccount, propertyIds: string[]) => void;
    onError?: (code: OAuthErrorCode) => void;
    onDisconnect?: () => void;
    selectedPropertyIds: string[];
    /** Restores the connected screen when the wizard reopens mid-setup. */
    connectedEmail?: string | null;
    /** MOCK: dev-only override so reviewers can force each failure path. */
    forcedError?: OAuthErrorCode | null;
}

export function MockGoogleOAuth({
    scopes,
    onSuccess,
    onError,
    onDisconnect,
    selectedPropertyIds,
    connectedEmail = null,
    forcedError = null
}: MockGoogleOAuthProps) {
    // Resume straight into the connected screen when the wizard already holds a
    // connection, rather than making the user redo consent on every reopen.
    const [state, dispatch] = useReducer(reducer, connectedEmail, (email): State => {
        const restored = email ? MOCK_ACCOUNTS.find((a) => a.email === email) : undefined;
        return restored ? { stage: "SUCCESS", account: restored, error: null, finalising: false } : INITIAL;
    });
    const [toast, setToast] = useState<string | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const timers = useRef<number[]>([]);

    const after = useCallback((ms: number, fn: () => void) => {
        const id = window.setTimeout(fn, ms);
        timers.current.push(id);
    }, []);

    useEffect(
        () => () => {
            timers.current.forEach((id) => window.clearTimeout(id));
        },
        []
    );

    const fail = useCallback(
        (code: OAuthErrorCode) => {
            dispatch({ type: "FAIL", error: code });
            onError?.(code);
            triggerRef.current?.focus();
        },
        [onError]
    );

    const launch = useCallback(() => {
        dispatch({ type: "LAUNCH" });
        after(MOCK_DELAYS.launchPopup, () => dispatch({ type: "POPUP_READY" }));
    }, [after]);

    const grantConsent = useCallback(
        (account: MockGoogleAccount) => {
            dispatch({ type: "CONSENT_GRANTED" });
            after(MOCK_DELAYS.consentProcessing, () => {
                dispatch({ type: "FINALISE" });
                after(MOCK_DELAYS.wizardConnecting, () => {
                    // Failure paths that only surface after the token comes back.
                    if (forcedError === "NO_PROPERTIES" || forcedError === "INSUFFICIENT_PERMISSION") {
                        fail(forcedError);
                        return;
                    }
                    dispatch({ type: "SUCCEED" });
                    // Report the connection before any property is picked, so the wizard
                    // can persist it and restore this screen if the modal is reopened.
                    onSuccess(account, []);
                });
            });
        },
        [after, fail, forcedError, onSuccess]
    );

    const showToast = (message: string) => {
        setToast(message);
        after(2200, () => setToast(null));
    };

    const popupOpen = popupIsOpen(state.stage);

    const errorCopy = state.error ? COPY.wizard.oauth.errors[state.error] : null;

    return (
        <div>
            {state.stage === "SUCCESS" && state.account ? (
                <>
                    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                        <CheckCircle2 className="h-4 w-4 flex-none text-emerald-600" strokeWidth={2} />
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-[13px] font-semibold text-emerald-900">
                                {COPY.wizard.step1.connectedAs(state.account.email)}
                            </p>
                            <p className="text-[12px] text-emerald-800">{COPY.wizard.step1.connectedScope}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                dispatch({ type: "RESET" });
                                onDisconnect?.();
                            }}
                            className="rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-[12px] font-medium text-emerald-800 transition-colors hover:bg-emerald-50">
                            {COPY.wizard.step1.disconnect}
                        </button>
                    </div>

                    <PropertyPicker
                        accounts={MOCK_PROPERTY_ACCOUNTS}
                        selectedIds={selectedPropertyIds}
                        onToggle={(p) => {
                            const next = selectedPropertyIds.includes(p.id)
                                ? selectedPropertyIds.filter((id) => id !== p.id)
                                : [...selectedPropertyIds, p.id];
                            onSuccess(state.account as MockGoogleAccount, next);
                        }}
                    />
                </>
            ) : (
                <>
                    <button
                        ref={triggerRef}
                        type="button"
                        onClick={launch}
                        disabled={popupOpen || state.finalising}
                        className="inline-flex items-center gap-2.5 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-[13px] font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-60">
                        {popupOpen || state.finalising ? (
                            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                        ) : (
                            <ProviderMark />
                        )}
                        {popupOpen ? COPY.wizard.step1.connecting : COPY.wizard.step1.connectCta}
                    </button>
                    <p className="mt-3 text-[12.5px] text-gray-500">{COPY.wizard.step1.reassurance}</p>

                    {state.finalising && (
                        <p className="mt-3 inline-flex items-center gap-2 text-[12.5px] text-gray-600">
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-geekblue-500" />
                            {COPY.wizard.step1.wizardConnecting}
                        </p>
                    )}

                    {errorCopy && !popupOpen && !state.finalising && (
                        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3.5">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-600" strokeWidth={2} />
                                <div>
                                    <p className="text-[12.5px] leading-relaxed text-amber-900">{errorCopy.message}</p>
                                    <button
                                        type="button"
                                        onClick={launch}
                                        className="mt-2.5 rounded-md bg-amber-600 px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-amber-700">
                                        {errorCopy.action}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {popupOpen && (
                <MockPopup onScrimClose={() => fail("POPUP_CLOSED")}>
                    {state.stage === "LAUNCHING" && <PopupSpinner label={COPY.wizard.oauth.processing} />}
                    {state.stage === "ACCOUNT_CHOOSER" && (
                        <AccountChooser
                            onPick={(a) => after(MOCK_DELAYS.accountSelect, () => dispatch({ type: "PICK_ACCOUNT", account: a }))}
                            onUseAnother={() => showToast(COPY.wizard.oauth.chooser.useAnotherUnavailable)}
                        />
                    )}
                    {state.stage === "CONSENT" && state.account && (
                        <ConsentScreen
                            account={state.account}
                            scopes={scopes}
                            onCancel={() => fail("DENIED")}
                            onConfirm={() => grantConsent(state.account as MockGoogleAccount)}
                        />
                    )}
                    {state.stage === "PROCESSING" && <PopupSpinner label={COPY.wizard.oauth.processing} />}
                </MockPopup>
            )}

            {toast && (
                <div className="fixed bottom-6 left-1/2 z-[1300] -translate-x-1/2 rounded-lg bg-gray-900 px-4 py-2 text-[12.5px] text-white shadow-lg">
                    {toast}
                </div>
            )}
        </div>
    );
}

export const DEFAULT_OAUTH_SCOPES = [ANALYTICS_READONLY_SCOPE];

export default MockGoogleOAuth;
